import React from 'react';
import DOMPurify from 'dompurify';
import { Package } from 'lucide-react';

/**
 * ============================================================================
 * TextBlock - Bloc Texte Riche
 * ============================================================================
 * 
 * DESIGN SYSTEM CONTRACT:
 * ✅ width: 100% du parent (le layout contrôle la largeur)
 * ✅ padding interne configurable
 * ✅ styles internes (background, typography)
 * ❌ INTERDIT: max-width basé sur viewport
 * ❌ INTERDIT: width en % du viewport
 * ❌ INTERDIT: margin auto pour contrôler la largeur globale
 * 
 * RÈGLE: Le LAYOUT parent définit la largeur, pas le bloc.
 */
const TextBlock = ({ block }) => {
  const { title, subtitle, text, background_color, style } = block.content || {};

  // Sanitize HTML content for security
  // Autoriser les styles inline pour police, taille, couleur
  const sanitizedText = text ? DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'strike', 
      'ul', 'ol', 'li', 'span', 'font', 'sup', 'sub',
      'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
    ],
    ALLOWED_ATTR: ['style', 'class', 'color', 'size', 'face'],
    ALLOWED_STYLES: {
      '*': {
        'font-family': [/.*/],
        'font-size': [/^\d+(?:px|em|rem|%)$/],
        'color': [/^#[0-9a-fA-F]{3,6}$/, /^rgb\(/, /^rgba\(/],
        'text-align': [/^left$/, /^center$/, /^right$/]
      }
    }
  }) : '';

  // Style placeholder : affichage spécial pour les pages en construction
  if (style === 'placeholder') {
    return (
      <div 
        className="text-block text-block-placeholder"
        data-testid="text-block-placeholder"
        data-block-type="text"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '48px 16px'
        }}
      >
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          {/* Icône */}
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-muted-foreground" />
          </div>
          
          {/* Titre */}
          {title && (
            <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight mb-4">
              {title}
            </h1>
          )}
          
          {/* Texte placeholder */}
          {text && (
            <p className="text-lg text-muted-foreground mb-8">
              {text}
            </p>
          )}
          
          {/* Indicateur visuel */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted/50 rounded-full text-sm text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            En cours de préparation
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="text-block"
      data-testid="text-block"
      data-block-type="text"
      style={{
        // ============================================================
        // DESIGN SYSTEM CONTRACT: width = 100% du parent
        // Le layout parent contrôle la largeur, pas ce composant
        // ============================================================
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: background_color || 'transparent',
        padding: '32px 16px'
      }}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Titre optionnel (nouveau champ) */}
        {title && (
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-6 text-center">
            {title}
          </h2>
        )}
        
        {/* Subtitle (ancien champ, conservé pour compatibilité) */}
        {subtitle && (
          <p className="text-base md:text-lg text-muted-foreground text-center mb-6">
            {subtitle}
          </p>
        )}
        
        {/* Contenu riche avec HTML sanitizé */}
        {text && (
          <div 
            className="prose prose-lg max-w-none text-muted-foreground break-words rich-text-content"
            dangerouslySetInnerHTML={{ __html: sanitizedText }}
            style={{
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}
          />
        )}
      </div>
    </div>
  );
};

export default TextBlock;